import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import {
  enterFullscreen,
  fullscreenAvailable,
  fullscreenElement,
  isPortraitViewport,
  isTouchDevice,
  leaveFullscreen,
  lockLandscapeForTouchDevice,
  unlockOrientation,
} from './fullscreenOrientation';

type FullscreenGameShellProps = {
  gameRef: RefObject<{ scale: { refresh: () => void } } | null>;
  toolbar: ReactNode;
  children: ReactNode;
  className?: string;
};

export function FullscreenGameShell({ gameRef, toolbar, children, className = '' }: FullscreenGameShellProps) {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [fullscreenSupported, setFullscreenSupported] = useState(false);
  const [orientationMessage, setOrientationMessage] = useState<string | null>(null);

  useEffect(() => {
    if (shellRef.current) setFullscreenSupported(fullscreenAvailable(shellRef.current));
    const refreshGame = () => requestAnimationFrame(() => gameRef.current?.scale.refresh());
    const handleFullscreenChange = () => {
      const gameIsFullscreen = fullscreenElement() === shellRef.current;
      setFullscreen(gameIsFullscreen);
      if (!gameIsFullscreen) {
        unlockOrientation();
        setOrientationMessage(null);
      }
      refreshGame();
    };
    const handleOrientationChange = () => {
      if (!isPortraitViewport()) setOrientationMessage(null);
      refreshGame();
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    window.addEventListener('resize', handleOrientationChange);
    screen.orientation?.addEventListener('change', handleOrientationChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      window.removeEventListener('resize', handleOrientationChange);
      screen.orientation?.removeEventListener('change', handleOrientationChange);
      if (fullscreenElement() === shellRef.current) unlockOrientation();
    };
  }, [gameRef]);

  const toggleFullscreen = async () => {
    if (!shellRef.current || !fullscreenAvailable(shellRef.current)) return;
    try {
      if (fullscreenElement() === shellRef.current) {
        await leaveFullscreen();
        return;
      }
      setOrientationMessage(null);
      await enterFullscreen(shellRef.current);
      const lockResult = await lockLandscapeForTouchDevice();
      if (isTouchDevice() && isPortraitViewport() && lockResult !== 'locked') {
        setOrientationMessage('Tarayıcın ekranı otomatik çeviremiyor. Oyuna devam etmek için telefonunu yatay çevir.');
      }
      requestAnimationFrame(() => gameRef.current?.scale.refresh());
    } catch {
      setFullscreen(false);
      setOrientationMessage('Tam ekran başlatılamadı. Tarayıcı ayarlarından tam ekran iznini kontrol et.');
    }
  };

  return (
    <div className={`game-shell ${className}`.trim()} ref={shellRef}>
      <div className="game-toolbar" aria-label="Oyun bilgileri">
        {toolbar}
        {fullscreenSupported && (
          <button
            className="fullscreen-button"
            type="button"
            onClick={() => void toggleFullscreen()}
            aria-pressed={fullscreen}
            aria-label={fullscreen ? 'Tam ekrandan çık' : 'Oyunu tam ekran yap'}
          >
            <span aria-hidden="true">{fullscreen ? '×' : '⛶'}</span>
            {fullscreen ? 'Tam ekrandan çık' : 'Tam ekran'}
          </button>
        )}
      </div>
      {orientationMessage && <p className="orientation-lock-notice" role="status">{orientationMessage}</p>}
      {children}
    </div>
  );
}
